class ProductPdpV2 extends HTMLElement {
  static popStateBound = false;
  static fetchAbort = null;

  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    this.mode = this.dataset.mode;
    this.productId = this.dataset.productId || null;
    this.selectedOptions = {};
    this.variants = [];
    this.optionNames = [];
    this.recipeOptionName = "Recipe";
    this.sizeOptionName = "Size";
    this.bundleOptionName = "Bundle Size";
    this.activeVariantId = null;
    this.isFetching = false;
    this.eventsBound = false;
    this.onSelectorClick = this.onSelectorClick.bind(this);
    this.onStickySelectorChange = this.onStickySelectorChange.bind(this);
    this.onSellingPlanChange = this.onSellingPlanChange.bind(this);
  }

  connectedCallback() {
    if (this.mode !== "multi_variant") return;

    this.discoverSectionIds();
    this.loadVariantData();
    this.initSelectedOptionsFromDom();
    this.bindEvents();

    const params = new URLSearchParams(window.location.search);
    const urlVariantId = params.get("variant");
    if (urlVariantId) {
      this.syncFromUrl({ replace: true, fetch: false });
      const sellingPlanId = params.get("selling_plan");
      if (sellingPlanId) {
        ProductPdpV2.applySellingPlan(this.sectionId, sellingPlanId);
      }
    } else {
      const current = this.getCurrentVariant();
      if (current) this.activeVariantId = current.id;
    }
  }

  disconnectedCallback() {
    if (!this.eventsBound) return;
    this.removeEventListener("click", this.onSelectorClick);
    this.removeEventListener("change", this.onSellingPlanChange);
    document.removeEventListener("change", this.onStickySelectorChange);
    this.eventsBound = false;
  }

  discoverSectionIds() {
    const ids = new Set();
    document.querySelectorAll("[data-pdp-variant-section]").forEach((el) => {
      const id = el.getAttribute("data-pdp-variant-section");
      if (id) ids.add(id);
    });
    this.sectionIds = Array.from(ids);
    if (!this.sectionIds.includes(this.sectionId)) {
      this.sectionIds.unshift(this.sectionId);
    }
  }

  loadVariantData() {
    const script = document.getElementById(`VariantData-${this.sectionId}`);
    if (!script) return;

    try {
      const data = JSON.parse(script.textContent);
      this.variants = data.variants || [];
      this.optionNames = data.optionNames || [];
      this.recipeOptionName = data.recipeOptionName || this.recipeOptionName;
      this.sizeOptionName = data.sizeOptionName || this.sizeOptionName;
      this.bundleOptionName = data.bundleOptionName || this.bundleOptionName;
      this.productId = data.productId || this.productId;
      this.sectionId = data.sectionId || this.sectionId;
    } catch (e) {
      console.error("product-pdp-v2: failed to parse variant data", e);
    }
  }

  initSelectedOptionsFromDom() {
    this.selectedOptions = {};
    this.querySelectorAll("[data-option-name][data-option-value].is-selected").forEach(
      (el) => {
        this.selectedOptions[el.dataset.optionName] = el.dataset.optionValue;
      },
    );

    if (Object.keys(this.selectedOptions).length === 0) {
      const current = this.getCurrentVariant();
      if (current?.options) {
        this.optionNames.forEach((name) => {
          if (current.options[name] != null) {
            this.selectedOptions[name] = current.options[name];
          }
        });
      }
    }
  }

  bindEvents() {
    if (this.eventsBound) return;
    this.eventsBound = true;
    this.addEventListener("click", this.onSelectorClick);
    this.addEventListener("change", this.onSellingPlanChange);
    document.addEventListener("change", this.onStickySelectorChange);

    if (!ProductPdpV2.popStateBound) {
      window.addEventListener("popstate", ProductPdpV2.handlePopState);
      ProductPdpV2.popStateBound = true;
    }
  }

  static handlePopState() {
    const host = document.querySelector(
      'product-pdp-v2[data-mode="multi_variant"]',
    );
    if (!host) return;
    host.syncFromUrl({ replace: false, fetch: true });
  }

  onSelectorClick(event) {
    const trigger = event.target.closest(
      "[data-option-name][data-option-value]",
    );
    if (!trigger || !this.contains(trigger)) return;

    event.preventDefault();
    const name = trigger.dataset.optionName;
    const value = trigger.dataset.optionValue;
    if (!name || value == null) return;

    this.selectOption(name, value, trigger);
  }

  onStickySelectorChange(event) {
    const trigger = event.target.closest("[data-pdp-sticky-option-name]");
    if (!trigger) return;

    const sectionEl = document.getElementById(`shopify-section-${this.sectionId}`);
    if (!sectionEl || !sectionEl.contains(trigger)) return;

    const name = trigger.dataset.pdpStickyOptionName;
    const value = trigger.value;
    if (!name || value == null) return;

    this.selectOption(name, value, trigger);
  }

  onSellingPlanChange(event) {
    const target = event.target;
    if (
      !target.matches(
        'input[name="selling_plan_select"], select[name="rtx_option_plans_dropdown"], input[name="selling_plan"]',
      )
    ) {
      return;
    }

    // Retextion updates the hidden selling_plan input from these controls; read it
    // after the current event settles, then reflect the plan in the URL alongside the variant.
    window.setTimeout(() => {
      const variantId = this.activeVariantId || this.getCurrentVariant()?.id;
      if (!variantId) return;
      ProductPdpV2.updateUrl(variantId, this.getSellingPlanId());
    }, 0);
  }

  selectOption(name, value, trigger) {
    const variant = this.resolveVariantForSelection(name, value);
    if (!variant) return;
    if (
      String(variant.id) === String(this.activeVariantId) &&
      trigger.classList.contains("is-selected")
    ) {
      return;
    }

    this.selectedOptions = { ...(variant.options || {}) };
    this.activeVariantId = variant.id;

    ProductPdpV2.fetchAndSwapSections({
      variantId: variant.id,
      sectionIds: this.sectionIds,
      mainSectionId: this.sectionId,
      sellingPlanId: this.getSellingPlanId(),
      replaceUrl: true,
    });
  }

  syncFromUrl({ replace, fetch }) {
    const params = new URLSearchParams(window.location.search);
    const variantId = params.get("variant");
    if (!variantId) return;

    if (fetch) {
      ProductPdpV2.fetchAndSwapSections({
        variantId: parseInt(variantId, 10),
        sectionIds: this.sectionIds,
        mainSectionId: this.sectionId,
        sellingPlanId: params.get("selling_plan") || this.getSellingPlanId(),
        replaceUrl: replace,
      });
      return;
    }

    const variant = this.variants.find((v) => String(v.id) === variantId);
    if (variant?.options) {
      this.optionNames.forEach((name) => {
        if (variant.options[name] != null) {
          this.selectedOptions[name] = variant.options[name];
        }
      });
      this.activeVariantId = variant.id;
    }

    if (replace) {
      ProductPdpV2.updateUrl(variantId, params.get("selling_plan"));
    }
  }

  getCurrentVariant() {
    const idInput = document.querySelector(
      `#ProductSection-${this.sectionId} input[name="id"], #ProductSection-${this.sectionId} select[name="id"]`,
    );
    const variantId = idInput ? parseInt(idInput.value, 10) : null;
    return (
      this.variants.find((v) => v.id === variantId) ||
      this.variants.find((v) => v.available) ||
      this.variants[0]
    );
  }

  findVariant() {
    return this.findVariantForOptions(this.selectedOptions);
  }

  findVariantForOptions(options, { exact = true } = {}) {
    return this.variants.find((variant) => {
      return this.optionNames.every((name) => {
        const selected = options[name];
        if (selected == null) return !exact;
        return variant.options?.[name] === selected;
      });
    });
  }

  getCurrentOptions() {
    const selectedFromDom = {};
    this.querySelectorAll("[data-option-name][data-option-value].is-selected").forEach(
      (el) => {
        selectedFromDom[el.dataset.optionName] = el.dataset.optionValue;
      },
    );
    if (Object.keys(selectedFromDom).length > 0) return selectedFromDom;
    if (Object.keys(this.selectedOptions).length > 0) return { ...this.selectedOptions };

    const current = this.getCurrentVariant();
    if (current?.options) return { ...current.options };
    return {};
  }

  findBestVariantForOptions(options, requiredNames = []) {
    const matchesRequired = (variant) => {
      return requiredNames.every((name) => {
        return options[name] == null || variant.options?.[name] === options[name];
      });
    };

    const scoreVariant = (variant) => {
      return this.optionNames.reduce((score, name) => {
        return score + (options[name] != null && variant.options?.[name] === options[name] ? 1 : 0);
      }, 0);
    };

    return this.variants
      .filter(matchesRequired)
      .sort((a, b) => {
        const scoreDiff = scoreVariant(b) - scoreVariant(a);
        if (scoreDiff !== 0) return scoreDiff;
        return Number(b.available) - Number(a.available);
      })[0];
  }

  resolveVariantForSelection(name, value) {
    const currentOptions = this.getCurrentOptions();
    const nextOptions = { ...currentOptions, [name]: value };
    const exactVariant = this.findVariantForOptions(nextOptions);
    if (exactVariant) return exactVariant;

    const requiredNames = [name];
    if (name !== this.recipeOptionName && currentOptions[this.recipeOptionName]) {
      requiredNames.unshift(this.recipeOptionName);
      nextOptions[this.recipeOptionName] = currentOptions[this.recipeOptionName];
    }

    return this.findBestVariantForOptions(nextOptions, requiredNames);
  }

  getSellingPlanId() {
    const input = document.querySelector(
      `#ProductSection-${this.sectionId} input[name="selling_plan"]`,
    );
    return input?.value || "";
  }

  static updateUrl(variantId, sellingPlanId, { push = false } = {}) {
    const url = new URL(window.location.href);
    url.searchParams.set("variant", String(variantId));
    if (sellingPlanId) {
      url.searchParams.set("selling_plan", sellingPlanId);
    } else {
      url.searchParams.delete("selling_plan");
    }
    const state = { variantId };
    if (push) {
      window.history.pushState(state, "", url.toString());
    } else {
      window.history.replaceState(state, "", url.toString());
    }
  }

  static setLoading(isLoading) {
    document
      .querySelectorAll("[data-pdp-buybox]")
      .forEach((el) => el.classList.toggle("is-pdp-loading", isLoading));
  }

  static async fetchAndSwapSections({
    variantId,
    sectionIds,
    mainSectionId,
    sellingPlanId,
    replaceUrl = true,
  }) {
    if (!variantId || !sectionIds?.length) return;

    if (ProductPdpV2.fetchAbort) {
      ProductPdpV2.fetchAbort.abort();
    }
    ProductPdpV2.fetchAbort = new AbortController();
    const { signal } = ProductPdpV2.fetchAbort;

    ProductPdpV2.setLoading(true);

    sectionIds.forEach((sectionId) => {
      const sectionEl = document.getElementById(`shopify-section-${sectionId}`);
      if (!sectionEl) return;
      if (window.jQuery) {
        window.jQuery(document).trigger({
          type: "shopify:section:unload",
          detail: { sectionId },
        });
      }
    });

    const url = new URL(window.location.pathname, window.location.origin);
    url.searchParams.set("variant", String(variantId));
    url.searchParams.set("sections", sectionIds.join(","));

    try {
      const response = await fetch(url.toString(), { signal });
      if (!response.ok) throw new Error("Section fetch failed");

      const sectionsHtml = await response.json();

      if (replaceUrl) {
        ProductPdpV2.updateUrl(variantId, sellingPlanId, { push: true });
      }

      const updatedSectionEls = [];

      sectionIds.forEach((sectionId) => {
        const html = sectionsHtml[sectionId];
        if (!html) return;
        const sectionEl = document.getElementById(`shopify-section-${sectionId}`);
        if (sectionEl) {
          sectionEl.innerHTML = html;
          updatedSectionEls.push(sectionEl);
        }
      });

      updatedSectionEls.forEach((sectionEl) => {
        ProductPdpV2.reexecuteScripts(sectionEl);
      });

      ProductPdpV2.reinitProductSection(mainSectionId);

      ProductPdpV2.applySellingPlan(mainSectionId, sellingPlanId);
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("product-pdp-v2: section fetch failed", error);
      }
    } finally {
      ProductPdpV2.setLoading(false);
      ProductPdpV2.fetchAbort = null;
    }
  }

  static reexecuteScripts(root) {
    if (!root) return;
    root.querySelectorAll("script").forEach((oldScript) => {
      if (oldScript.src) return;
      const type = (oldScript.getAttribute("type") || "text/javascript")
        .trim()
        .toLowerCase();
      const executableTypes = [
        "",
        "text/javascript",
        "application/javascript",
        "text/ecmascript",
        "application/ecmascript",
      ];
      if (!executableTypes.includes(type)) return;

      const code = oldScript.textContent?.trim();
      if (!code) return;
      try {
        const fn = new Function(code);
        fn();
      } catch (err) {
        console.error("product-pdp-v2: inline script error", err);
      }
    });
  }

  static reinitProductSection(mainSectionId) {
    if (typeof window.page?.Product === "function") {
      window.page.Product();
    }

    const sectionEl = document.getElementById(`shopify-section-${mainSectionId}`);
    if (sectionEl && window.jQuery) {
      window.jQuery(sectionEl).trigger("shopify:section:load");
    }
  }

  static applySellingPlan(mainSectionId, sellingPlanId) {
    if (!sellingPlanId) return;
    const input = document.querySelector(
      `#ProductSection-${mainSectionId} input[name="selling_plan"]`,
    );
    if (!input) return;
    input.value = sellingPlanId;
    input.dispatchEvent(new Event("change", { bubbles: true }));
  }
}

customElements.define("product-pdp-v2", ProductPdpV2);
